import type { ModelDefinition } from "@/models.js";

export const zaiModels = [
	{
		id: "glm-5.3-flash",
		name: "GLM-5.3 Flash",
		description:
			"Lightweight GLM-5.3 variant for efficient coding and long-horizon agent tasks with a 1M context window.",
		family: "zai",
		releasedAt: new Date("2026-08-26"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.3-flash",
				inputPrice: "0.15e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "required", "function"],
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5.3-flash",
				inputPrice: "0.15e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto"],
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "fireworks",
				externalId: "accounts/fireworks/models/glm-5p3-flash",
				inputPrice: "0.15e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				minCacheableTokens: 2048,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				// Below xhigh this deployment returns no reasoning text on
				// tool-call turns.
				reasoningEfforts: ["xhigh", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
			},
			{
				providerId: "inference.net",
				externalId: "glm-5.3-flash",
				inputPrice: "0.09e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "0.28e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 128000,
				quantization: "fp4",
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "together-ai",
				externalId: "zai-org/GLM-5.3-Flash",
				inputPrice: "0.15e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 943717,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
			},
		],
	},
	{
		id: "glm-5.3",
		name: "GLM-5.3",
		description:
			"Zhipu GLM-5.3 flagship coding model, post-trained on the GLM-5.2 base with stronger agentic coding and emergent cybersecurity capabilities and a 1M context window.",
		family: "zai",
		releasedAt: new Date("2026-08-14"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				// glm-5.3 dropped support for disabling thinking, so no "none"
				// tier: a request that omits thinking or sends thinking.type
				// "disabled" is rejected with code 1210, "This model always
				// engages in thinking and cannot be disabled; please use low,
				// high, or max".
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5.3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
					"response_format",
				],
			},
			{
				providerId: "baidu",
				externalId: "glm-5.3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// GLM-5.3 always thinks and rejects attempts to disable thinking.
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "alibaba",
				externalId: "glm-5.3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.28e-6",
				outputPrice: "4.4e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "1.131e-6",
						cachedInputPrice: "0.2262e-6",
						outputPrice: "3.957e-6",
					},
					{
						id: "eu-frankfurt",
						inputPrice: "1.131e-6",
						cachedInputPrice: "0.2262e-6",
						outputPrice: "3.957e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				// DashScope validates temperature against [0, 2) rather than GLM's
				// own [0, 1] ceiling, and above 1 the deployment intermittently
				// never emits a stop token, so the request hangs instead of 400ing.
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "fireworks",
				externalId: "accounts/fireworks/models/glm-5p3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				serviceTiers: ["priority"],
				contextSize: 1048576,
				maxOutput: 131072,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				// Below xhigh this deployment returns no reasoning text on
				// tool-call turns.
				reasoningEfforts: ["xhigh", "max"],
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
			},
			{
				providerId: "inference.net",
				externalId: "glm-5.3",
				inputPrice: "0.9e-6",
				cachedInputPrice: "0.15e-6",
				outputPrice: "3e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				quantization: "fp4",
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "mistral",
				externalId: "zai-glm-5-3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.14e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none", "required", "function"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "deepinfra",
				externalId: "zai-org/GLM-5.3",
				// Unreliable: repeated 60s stalls across chat, tool and caching
				// cases, and 429 engine_overloaded bursts.
				deactivatedAt: new Date("2026-09-23"),
				inputPrice: "1.2e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				quantization: "fp4",
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				// low intermittently returns tool calls with no reasoning text.
				reasoningEfforts: ["none", "high", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "together-ai",
				externalId: "zai-org/GLM-5.3",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 943717,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
			},
		],
	},
	{
		id: "glm-5.2",
		name: "GLM-5.2",
		description:
			"Zhipu GLM-5.2 flagship model for long-horizon coding and agentic engineering tasks with a 1M context window.",
		family: "zai",
		releasedAt: new Date("2026-06-13"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "high", "max"],
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				// JSON mode is unreliable on this deployment: roughly half of the
				// responses put the object in reasoning_content with empty content, and
				// the rest markdown-fence it. json_schema behaves the same way.
				jsonOutput: false,
			},
			{
				providerId: "canopywave",
				test: "skip", // over-reasons heavily and streams slowly (~1 tok/s), so the 60s streaming timeout is flaky
				externalId: "zai/glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5.2",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "1.26e-6",
				outputPrice: "3.96e-6",
				cachedInputPrice: "0.234e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "bytedance",
				externalId: "glm-5-2-260617",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1024000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
				healStreamingJsonOutput: true,
			},
			{
				providerId: "alibaba",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.28e-6",
				outputPrice: "4.4e-6",
				regions: [
					{ id: "singapore" },
					{ id: "eu-frankfurt" },
					{ id: "us-virginia" },
					{ id: "cn-beijing" },
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportsDeveloperRole: false,
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5.2",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "1.4e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				// The model card advertises 1,024K context, and Nebius accepted a
				// 52K-token prompt live (verified 2026-07-22).
				contextSize: 1048576,
				maxOutput: undefined,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "baidu",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				// Qianfan ignores reasoning_effort="none"; its thinking switch works.
				requiresDisableThinkingParam: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "tencent",
				externalId: "glm-5.2",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
				// Unlike Z.ai's own deployment, TokenHub's GLM mappings accept
				// response_format: json_schema with a 200 and then ignore it —
				// asked to extract a name and age under a strict schema they
				// answer in markdown prose. json_object is genuinely enforced.
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "glm-5.1",
		name: "GLM-5.1",
		description:
			"Zhipu GLM-5.1 flagship model engineered for long-horizon autonomous tasks with enhanced coding and agentic capabilities.",
		family: "zai",
		releasedAt: new Date("2026-04-07"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5.1",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5.1",
				inputPrice: "1.38e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				// novita's glm-5.1 reasons adaptively and omits reasoning_content
				// for simple prompts; no parameter forces it on
				reasoningOutput: "omit",
				vision: false,
				tools: true,
				jsonOutput: true,
				// JSON mode consistently markdown-fences the object on this
				// deployment, so normalize it defensively in both modes.
				healStreamingJsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "zai-org/GLM-5.1",
				deactivatedAt: new Date("2026-07-10"),
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 202752,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "zai-org/GLM-5.1",
				// DeepInfra retires this model on 2026-10-01 and silently
				// redirects requests to zai-org/GLM-5.3 afterwards.
				deactivatedAt: new Date("2026-10-01"),
				inputPrice: "1.05e-6",
				cachedInputPrice: "0.205e-6",
				outputPrice: "3.5e-6",
				requestPrice: "0",
				contextSize: 198000,
				maxOutput: 65536,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5.1",
				// embercloud returns 400 "Temporary routing error" on every request
				// for this model (verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.931e-6",
				outputPrice: "2.93e-6",
				cachedInputPrice: "0.173e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: false,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5.1",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "1.4e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: undefined,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "baidu",
				externalId: "glm-5.1",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				// Qianfan ignores reasoning_effort="none"; its thinking switch works.
				requiresDisableThinkingParam: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "tencent",
				externalId: "glm-5.1",
				inputPrice: "1.4e-6",
				cachedInputPrice: "0.26e-6",
				outputPrice: "4.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				// "none" and "minimal" are accepted but do not stop the model
				// thinking (both still report ~125 reasoning tokens), so only the
				// tiers that behave as advertised are declared.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "glm-5",
		name: "GLM-5",
		description: "Zhipu GLM-5 with advanced reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2026-02-15"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-5",
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				deactivatedAt: new Date("2026-04-22"),
				providerId: "together-ai",
				externalId: "zai-org/GLM-5",
				inputPrice: "1e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 202752,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-5",
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202800,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-5",
				inputPrice: "0.573e-6",
				outputPrice: "2.58e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.573e-6",
						outputPrice: "2.58e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.86e-6",
						outputPrice: "3.154e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				reasoningMaxTokens: true,
				reasoningEfforts: ["none", "minimal", "low", "medium", "high", "xhigh"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-5",
				// embercloud reasoning requests time out repeatedly in e2e
				stability: "unstable",
				inputPrice: "0.72e-6",
				outputPrice: "2.3e-6",
				cachedInputPrice: "0.144e-6",
				requestPrice: "0",
				contextSize: 203000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: false,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "nebius",
				externalId: "zai-org/GLM-5",
				deactivatedAt: new Date("2026-06-22"),
				inputPrice: "1.0e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "vertex-openai",
				externalId: "zai-org/glm-5-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "1e-6",
				cachedInputPrice: "0.1e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "baidu",
				externalId: "glm-5",
				// Use Qianfan's public list price. /v1/models may return an
				// account-specific promotional or negotiated rate instead.
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				// Qianfan ignores reasoning_effort="none"; its thinking switch works.
				requiresDisableThinkingParam: true,
				vision: false,
				tools: true,
				// The API advertises structured_outputs but Qianfan's own model page
				// lists Structured Output as unsupported for this one. Left off until a
				// live request settles it: an unsupported claim 400s, omitting it only
				// routes JSON-mode traffic elsewhere.
			},
			{
				providerId: "tencent",
				externalId: "glm-5",
				inputPrice: "1e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
				// This deployment hard-400s on json_schema rather than ignoring
				// it, matching TokenHub's model list which omits Structured Output
				// for GLM-5 alone.
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "glm-4.5",
		name: "GLM-4.5",
		description: "Zhipu GLM-4.5 with reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5",
				// Tool calling is unreliable: zai returns tool calls as inline
				// <tool_call> XML in message content instead of structured
				// tool_calls, so the gateway cannot surface them.
				stability: "unstable",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.5",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.6e-6",
				outputPrice: "2.2e-6",
				cachedInputPrice: "0.11e-6",
				requestPrice: "0",
				contextSize: 131000,
				maxOutput: 96000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Embercloud bug: tool_choice other than "auto" breaks streaming tool calls for this model
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.5v",
		name: "GLM-4.5V",
		description: "GLM-4.5 with vision support.",
		family: "zai",
		releasedAt: new Date("2025-08-11"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5v",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-4.5v",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 65536,
				maxOutput: 16000,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-air",
		name: "GLM-4.5 Air",
		description: "Lightweight GLM-4.5 variant.",
		family: "zai",
		releasedAt: new Date("2025-07-25"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-air",
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.1e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: true,
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.5-air",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.13e-6",
				outputPrice: "0.85e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 131000,
				maxOutput: 96000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.5-x",
		name: "GLM-4.5 X",
		description: "Extended GLM-4.5 with advanced reasoning.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-x",
				inputPrice: "2.2e-6",
				cachedInputPrice: "0.45e-6",
				outputPrice: "8.9e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				stability: "unstable", // unstable due to high limits
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-airx",
		name: "GLM-4.5 AirX",
		description: "Enhanced GLM-4.5 Air variant.",
		family: "zai",
		releasedAt: new Date("2025-07-28"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-airx",
				inputPrice: "1.1e-6",
				cachedInputPrice: "0.22e-6",
				outputPrice: "4.5e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.5-flash",
		name: "GLM-4.5 Flash",
		description: "Free, fast GLM-4.5 model.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-08-13"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.5-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.7",
		name: "GLM-4.7",
		description: "Latest GLM with enhanced reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				test: "skip",
				externalId: "glm-4.7",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "novita",
				test: "skip", // skip tests due to reasoning issues
				externalId: "zai-org/glm-4.7",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 128000,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				externalId: "zai-glm-4.7",
				inputPrice: "2.25e-6",
				outputPrice: "2.75e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "bytedance",
				externalId: "glm-4-7-251222",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false, // Returns JSON wrapped in markdown code blocks
			},
			{
				providerId: "together-ai",
				test: "skip", // skip tests due to reasoning issues
				externalId: "zai-org/GLM-4.7",
				inputPrice: "0.45e-6",
				outputPrice: "2.0e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: false,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-4.7",
				inputPrice: "0.431e-6",
				outputPrice: "2.007e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.431e-6",
						outputPrice: "2.007e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.574e-6",
						outputPrice: "2.294e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "embercloud",
				externalId: "glm-4.7",
				// embercloud reasoning requests time out repeatedly in e2e
				stability: "unstable",
				inputPrice: "0.38e-6",
				outputPrice: "1.98e-6",
				cachedInputPrice: "0.19e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
			{
				providerId: "vertex-openai",
				externalId: "zai-org/glm-4.7-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.6e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.7-flashx",
		name: "GLM-4.7 FlashX",
		description: "Lightweight, high-speed GLM-4.7 variant.",
		family: "zai",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.7-flashx",
				// Frequently times out on multi-turn tool call requests
				stability: "unstable",
				inputPrice: "0.07e-6",
				cachedInputPrice: "0.01e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		// Free GLM-4.7 Flash, served by zai at zero cost. Kept as a separate root
		// model from the paid embercloud mapping below so `free: true` strictly
		// implies every provider mapping is free (no free model carries a paid
		// provider). This is the model end-user test/sandbox wallets can use.
		id: "glm-4.7-flash-free",
		name: "GLM-4.7 Flash (Free)",
		description: "Free, lightweight GLM-4.7 model.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.7-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		// Paid GLM-4.7 Flash, served by embercloud (PAYG). Not free.
		id: "glm-4.7-flash",
		name: "GLM-4.7 Flash",
		description: "Lightweight, high-speed GLM-4.7 model.",
		family: "zai",
		stability: "unstable",
		releasedAt: new Date("2025-12-22"),
		providers: [
			{
				providerId: "embercloud",
				externalId: "glm-4.7-flash",
				// embercloud rate-limits this model on every request (429 even for
				// single spaced-out calls, verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.06e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 131000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"logprobs",
					"top_logprobs",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "glm-4.6",
		name: "GLM-4.6",
		description: "Updated GLM with reasoning capabilities.",
		family: "zai",
		releasedAt: new Date("2025-09-30"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				// zai hangs indefinitely on tool_choice "required" and named
				// function choices for glm-4.6 (verified live 2026-07-14)
				supportedToolChoices: ["auto", "none"],
				webSearch: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				deactivatedAt: new Date("2026-01-20"),
				externalId: "zai-glm-4.6",
				inputPrice: "2.25e-6",
				outputPrice: "2.75e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "glm-4.6",
				inputPrice: "0.431e-6",
				outputPrice: "2.007e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.431e-6",
						outputPrice: "2.007e-6",
					},
					{
						name: "Over 32K",
						upToTokens: Infinity,
						inputPrice: "0.574e-6",
						outputPrice: "2.294e-6",
					},
				],
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 202752,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				test: "skip",
				externalId: "zai-org/glm-4.6",
				inputPrice: "0.55e-6",
				cachedInputPrice: "0.11e-6",
				outputPrice: "2.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: undefined,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4-32b-0414-128k",
		name: "GLM-4 32B (0414-128k)",
		description: "GLM-4 32B instruction-tuned model.",
		family: "zai",
		releasedAt: new Date("2025-04-14"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4-32b-0414-128k",
				inputPrice: "0.1e-6",
				cachedInputPrice: "0",
				outputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				reasoning: false,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v",
		name: "GLM-4.6V",
		description:
			"Flagship vision-language model (106B) with native function calling support.",
		family: "zai",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.05e-6",
				outputPrice: "0.9e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "zai-org/glm-4.6v",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.055e-6",
				outputPrice: "0.9e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 16000,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v-flashx",
		name: "GLM-4.6V FlashX",
		description: "Fast vision-language model with extended capabilities.",
		family: "zai",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v-flashx",
				inputPrice: "0.04e-6",
				cachedInputPrice: "0.004e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "glm-4.6v-flash",
		name: "GLM-4.6V Flash",
		description:
			"Ultra-fast, lightweight vision-language model (9B) for low-latency workloads.",
		family: "zai",
		free: true,
		stability: "unstable",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "zai",
				externalId: "glm-4.6v-flash",
				deactivatedAt: new Date("2026-06-28"),
				inputPrice: "0",
				cachedInputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
					"reasoning",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "cogview-4",
		name: "CogView-4",
		description:
			"Z.AI's CogView-4 text-to-image generation model with bilingual support and excellent text rendering.",
		family: "zai",
		output: ["text", "image"],
		releasedAt: new Date("2025-03-04"),
		providers: [
			{
				test: "skip",
				providerId: "zai",
				externalId: "cogView-4-250304",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.01",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "glm-image",
		name: "GLM-Image",
		description:
			"Z.AI's GLM-Image text-to-image generation model with hybrid auto-regressive architecture, excellent for text-rendering and knowledge-intensive generation.",
		family: "zai",
		output: ["text", "image"],
		releasedAt: new Date("2025-01-14"),
		providers: [
			{
				test: "skip",
				providerId: "zai",
				externalId: "glm-image",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.015",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "glm-5-turbo",
		name: "GLM-5 Turbo",
		description:
			"Zhipu's speed-optimised GLM-5 variant with a 200K context window and deep reasoning.",
		family: "zai",
		releasedAt: new Date("2026-03-15"),
		providers: [
			{
				providerId: "tencent",
				externalId: "glm-5-turbo",
				inputPrice: "1.2e-6",
				cachedInputPrice: "0.24e-6",
				outputPrice: "4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				// "none"/"minimal" are accepted but still return ~140-200
				// reasoning tokens, so the off tiers are not offered.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "glm-5v-turbo",
		name: "GLM-5V Turbo",
		description:
			"Zhipu's speed-optimised multimodal GLM-5 variant that accepts image input alongside text, with a 200K context window.",
		family: "zai",
		releasedAt: new Date("2026-04-01"),
		providers: [
			{
				providerId: "tencent",
				externalId: "glm-5v-turbo",
				inputPrice: "1.2e-6",
				cachedInputPrice: "0.24e-6",
				outputPrice: "4e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: false,
			},
		],
	},
] as const satisfies ModelDefinition[];
